import { FC, useState, ReactNode } from 'react';
import classnames from 'classnames';

import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// Components
import { TooltipLoader } from '../../global/TooltipLoader';
import { Link } from 'dibs-elements/exports/Link';
import { Menu } from 'dibs-elements/exports/Menu';
import { useMenuTrigger } from 'dibs-elements/exports/useMenuTrigger';

import styles from './PdpRespIconBubbles.scss';

const TOOLTIP_HIDE_TIMEOUT = 200;

type Props = {
    icon: FC<{ className?: string }>;
    label: string;
    children: ReactNode | ReactNode[];
};

export const PdpRespIconBubbleWithTooltip: FC<Props> = ({ icon: Icon, label, children }) => {
    const { isDesktop } = useServerVarsContext();
    const [showTooltip, setShowTooltip] = useState(false);

    const handleHideTooltip = (): void => {
        setShowTooltip(false);
    };

    const [debouncedHideTooltip, cancelDebouncedHideTooltip] = useDebouncedCallback(
        handleHideTooltip,
        TOOLTIP_HIDE_TIMEOUT
    );

    const handleShowTooltip = (): void => {
        cancelDebouncedHideTooltip();
        setShowTooltip(true);
    };

    const tooltipTriggerRef = useMenuTrigger(showTooltip, handleShowTooltip, handleHideTooltip);

    return (
        <div
            onMouseEnter={handleShowTooltip}
            onMouseLeave={debouncedHideTooltip}
            className={classnames(styles.tooltipWrapper, 'enable-click-on-preview')}
            data-tn="icon-bubble-with-tooltip"
        >
            {/* Icon */}
            <Link
                onClick={handleShowTooltip}
                linkRef={tooltipTriggerRef}
                className={classnames(styles.bubble, {
                    [styles.withBorder]: !isDesktop,
                })}
                ariaLabel={label}
            >
                <Icon className={styles.icon} />
            </Link>

            {/* Tooltip */}
            <TooltipLoader
                load={showTooltip}
                isVisible={showTooltip}
                closeOnOutsideClick
                onClose={handleHideTooltip}
                type="autoWidth"
            >
                <Menu onClose={handleHideTooltip} isOpen={showTooltip}>
                    {children}
                </Menu>
            </TooltipLoader>
        </div>
    );
};
